import React, { useState, useEffect, useCallback } from 'react';
import './styles/global.css';
import TabBar from './components/TabBar';
import HomePage from './pages/HomePage';
import ObjectsPage from './pages/ObjectsPage';
import QuestionPage from './pages/QuestionPage';
import EventsPage from './pages/EventsPage';
import LibraryPage from './pages/LibraryPage';
import PaywallPage from './pages/PaywallPage';
import OnboardingPage from './pages/OnboardingPage';
import AiAssistant from './components/AiAssistant';
import { getEvents, getSubscription, markNotificationsRead, postWelcome, getWelcomeStatus } from './api/client';

// Parse startapp deep link parameter
function getStartParam() {
  try {
    const initData = window.Telegram?.WebApp?.initDataUnsafe;
    if (initData?.start_param) return initData.start_param;
    // Fallback: check URL params and hash
    const url = new URL(window.location.href);
    const fromSearch = url.searchParams.get('startapp') || url.searchParams.get('tgWebAppStartParam') || '';
    if (fromSearch) return fromSearch;
    // Check hash fragment (Telegram sometimes puts params there)
    const hash = window.location.hash;
    const match = hash.match(/[?&]tgWebAppStartParam=([^&]*)/);
    if (match) return decodeURIComponent(match[1]);
    return '';
  } catch { return ''; }
}

export default function App() {
  const [tab, setTab] = useState('home');
  const [eventCount, setEventCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [subStatus, setSubStatus] = useState(null); // null = loading, true = active, false = blocked
  const [onboardingDone, setOnboardingDone] = useState(null);
  const [exitPreview, setExitPreview] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [startParam] = useState(() => {
    const p = getStartParam();
    console.log('[DeepLink] startParam:', p);
    return p;
  });

  // Pull-to-refresh state
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = React.useRef(0);
  const isPulling = React.useRef(false);
  const PULL_THRESHOLD = 80;

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
      tg.setHeaderColor('#0D0D0D');
      tg.setBackgroundColor('#0D0D0D');

      // Велком-DM в бот при открытии аппки (как в немецком): просим право писать и
      // пингуем бэкенд (идемпотентно — сервер шлёт сообщение только один раз).
      // Спрашиваем ЗАНОВО при каждом заходе (раз за сессию), пока бэкенд не подтвердит
      // доставку велкома — иначе один случайный отказ терял человека навсегда.
      postWelcome().catch(() => {});
      (async () => {
        try {
          if (!tg.requestWriteAccess || sessionStorage.getItem('fixhub_write_asked')) return;
          const st = await getWelcomeStatus();
          if (st.sent) return;
          sessionStorage.setItem('fixhub_write_asked', '1');
          tg.requestWriteAccess(() => { postWelcome().catch(() => {}); });
        } catch (e) { /* ignore */ }
      })();
      // Fullscreen mode (Bot API 8.0+)
      if (tg.requestFullscreen) {
        tg.requestFullscreen();
      }
      // Disable swipe-to-close so we can do pull-to-refresh
      if (tg.disableVerticalSwipes) {
        tg.disableVerticalSwipes();
      }

      // Apply Telegram safe area insets as CSS variables
      const applySafeArea = () => {
        const sa = tg.safeAreaInset || {};
        const csa = tg.contentSafeAreaInset || {};
        const topInset = (sa.top || 0) + (csa.top || 0);
        const bottomInset = (sa.bottom || 0) + (csa.bottom || 0);
        document.documentElement.style.setProperty('--tg-safe-top', topInset + 'px');
        document.documentElement.style.setProperty('--tg-safe-bottom', bottomInset + 'px');
      };

      // Apply immediately and on events
      applySafeArea();
      tg.onEvent?.('safeAreaChanged', applySafeArea);
      tg.onEvent?.('contentSafeAreaChanged', applySafeArea);
      tg.onEvent?.('fullscreenChanged', applySafeArea);
      // Re-apply after a short delay (fullscreen may take time)
      setTimeout(applySafeArea, 300);
      setTimeout(applySafeArea, 1000);
    }
    // Deep links
    if (startParam && startParam.startsWith('q')) {
      setTab('question');
    } else if (startParam === 'objects') {
      setTab('objects');
    } else if (startParam === 'events') {
      setTab('events');
    }
  }, [startParam]);

  // Check subscription
  const checkSubscription = useCallback(() => {
    getSubscription()
      .then(data => {
        setSubStatus(data.active);
        setOnboardingDone(data.onboarding_complete);
        // Show notification if any
        if (data.notifications && data.notifications.length > 0) {
          setNotification(data.notifications[0].text);
          markNotificationsRead().catch(() => {});
        }
      })
      .catch(() => setSubStatus(prev => prev === null ? false : prev));
  }, []);

  // Check on mount
  useEffect(() => { checkSubscription(); }, [checkSubscription]);

  // Re-check subscription every 30 seconds
  useEffect(() => {
    const timer = setInterval(checkSubscription, 30000);
    return () => clearInterval(timer);
  }, [checkSubscription]);

  // Auto-hide notification after 10 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const loadEventCount = useCallback(() => {
    getEvents('status=upcoming').then(data => {
      setEventCount(data.counts?.total || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (subStatus) loadEventCount(); }, [loadEventCount, subStatus]);

  // Refresh event count every 30 seconds
  useEffect(() => {
    if (!subStatus) return;
    const timer = setInterval(loadEventCount, 30000);
    return () => clearInterval(timer);
  }, [loadEventCount, subStatus]);

  // Pull-to-refresh handlers
  const handleTouchStart = useCallback((e) => {
    if (window.scrollY === 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling.current) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (dy > 0 && window.scrollY === 0) {
      setPulling(true);
      setPullDistance(Math.min(dy * 0.5, 120));
    } else {
      setPulling(false);
      setPullDistance(0);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD) {
      // Trigger refresh
      setRefreshKey(k => k + 1);
      loadEventCount();
      checkSubscription();
    }
    isPulling.current = false;
    setPulling(false);
    setPullDistance(0);
  }, [pullDistance, loadEventCount, checkSubscription]);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setRefreshKey(k => k + 1);
    loadEventCount();
  };

  // Превью воронки по deep-link (?startapp=funnel) — показать онбординг любому (вкл. админа), без записи в БД
  if ((startParam === 'funnel' || startParam === 'preview') && !exitPreview) {
    return <OnboardingPage preview onComplete={() => setExitPreview(true)} />;
  }

  // Loading
  if (subStatus === null) {
    return (
      <div style={{
        minHeight: '100dvh', background: 'var(--bg-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ color: 'var(--text-tertiary)', fontSize: 15 }}>Загрузка...</div>
      </div>
    );
  }

  // Paywall
  if (!subStatus) {
    return <PaywallPage />;
  }

  // Onboarding
  if (onboardingDone === false) {
    return <OnboardingPage onComplete={() => { setOnboardingDone(true); checkSubscription(); }} />;
  }

  const renderPage = () => {
    switch (tab) {
      case 'home': return <HomePage key={refreshKey} onNavigate={handleTabChange} />;
      case 'objects': return <ObjectsPage onNavigate={handleTabChange} />;
      case 'question': return <QuestionPage key={refreshKey} startParam={startParam} />;
      case 'events': return <EventsPage key={refreshKey} onNavigate={handleTabChange} />;
      case 'library': return <LibraryPage key={refreshKey} />;
      default: return <HomePage key={refreshKey} onNavigate={handleTabChange} />;
    }
  };

  return (
    <div
      style={{ minHeight: '100dvh', background: 'var(--bg-primary)' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {pulling && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: pullDistance, overflow: 'hidden',
          transition: pullDistance === 0 ? 'height 0.2s ease' : 'none',
        }}>
          <div style={{
            width: 28, height: 28,
            border: '3px solid var(--bg-elevated)',
            borderTopColor: pullDistance >= PULL_THRESHOLD ? 'var(--accent)' : 'var(--text-tertiary)',
            borderRadius: '50%',
            transform: `rotate(${pullDistance * 3}deg)`,
            transition: 'border-color 0.2s',
            animation: pullDistance >= PULL_THRESHOLD ? 'none' : 'none',
          }} />
        </div>
      )}
      {/* In-app notification banner */}
      {notification && (
        <div onClick={() => setNotification(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          padding: '14px 20px', paddingTop: 'calc(var(--tg-safe-top, 0px) + 14px)',
          background: 'linear-gradient(135deg, #FFCC00 0%, #FFB800 100%)',
          color: '#0D0D0D', fontSize: 14, fontWeight: 600, lineHeight: 1.4,
          textAlign: 'center', cursor: 'pointer',
          animation: 'slideDown 0.3s ease',
          boxShadow: '0 4px 20px rgba(255,204,0,0.3)',
        }}>
          {notification}
        </div>
      )}
      {renderPage()}

      {/* AI Assistant floating button — only on home tab */}
      {tab === 'home' && !showAssistant && (
        <div onClick={() => setShowAssistant(true)} style={{
          position: 'fixed',
          bottom: 'calc(86px + var(--tg-safe-bottom, 0px))',
          right: 16, zIndex: 99,
          width: 52, height: 52, borderRadius: 16,
          background: 'linear-gradient(135deg, var(--accent), #E6B800)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(255,204,0,0.35)',
          animation: 'fadeInUp 0.3s ease 0.5s both',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            <circle cx="12" cy="10" r="1" fill="#0A0A0A" stroke="none"/>
            <circle cx="8" cy="10" r="1" fill="#0A0A0A" stroke="none"/>
            <circle cx="16" cy="10" r="1" fill="#0A0A0A" stroke="none"/>
          </svg>
        </div>
      )}

      <AiAssistant visible={showAssistant} onClose={() => setShowAssistant(false)} />

      <TabBar active={tab} onChange={handleTabChange} eventCount={eventCount} />
      <style>{`@keyframes slideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }`}</style>
    </div>
  );
}
